import type { ApolloCache } from "@apollo/client";
import { isReference } from "@apollo/client/utilities";

type CacheWithReadField = ApolloCache & {
  readField<T = unknown>(
    fieldName: string,
    from: unknown,
    args?: Record<string, unknown>
  ): T | undefined;
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === "object" && !isReference(value);

const readPlainField = <T = unknown>(
  from: unknown,
  fieldName: string
): T | undefined => {
  if (!isPlainObject(from)) {
    return undefined;
  }
  if (!(fieldName in from)) {
    return undefined;
  }
  return from[fieldName] as T | undefined;
};

/** Apollo's cache typing omits readField on ApolloCache in some TS versions. */
export const readCacheField = <T = unknown>(
  cache: ApolloCache,
  fieldName: string,
  from: unknown,
  args?: Record<string, unknown>
): T | undefined => {
  const plain = readPlainField<T>(from, fieldName);
  if (plain !== undefined) {
    return plain;
  }

  try {
    const fromCache = (cache as CacheWithReadField).readField<T>(
      fieldName,
      from,
      args
    );
    if (fromCache !== undefined) {
      return fromCache;
    }
  } catch {
    // Fall through to plain-object read.
  }

  return readPlainField<T>(from, fieldName);
};
